import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  changeComplimentStatus,
  complimentsQuery,
  createCompliment,
  removeCompliment,
  type Compliment,
} from '../../api/settingsEaseedeskFeedbackCompliments'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Feedback ticket compliments.
 *
 * Element cu element fata de `version-3/easeedesk_settings/feedback/
 * feedback_compliments.php`, si gest cu gest fata de
 * `public/js/views/easeedesk_settings/feedback/feedback_compliments-1.js`.
 *
 * Cuvintele bune pe care le poate bifa clientul cand lauda pe cineva. Tabela
 * din spate se cheama `..._feedback_responsible_attributes` - de-aia coloana
 * din ecran scrie "Attribute name", desi fila se cheama compliments.
 */
function ComplimentRow({ compliment, onReload }: { compliment: Compliment; onReload(): void }) {
  const flip = useMutation({
    mutationFn: (on: boolean) => changeComplimentStatus(compliment.ID, on),
    onSuccess: () => {
      toast('Setting changed', 'Success')
      onReload()
    },
    onError: () => {
      toast('Something went wrong.', 'Error', 'error')
      onReload()
    },
  })

  const remove = useMutation({
    mutationFn: () => removeCompliment(compliment.ID),
    onSuccess: () => {
      toast('Setting deleted', 'Success')
      onReload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <tr>
      <td>{compliment.attribute_name}</td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          name="status"
          checked={compliment.status === 1}
          onChange={(e) => flip.mutate(e.target.checked)}
        />
      </td>
      <td style={{ textAlign: 'center' }}>
        <div className="btn-group" role="group" aria-label="controls">
          <button
            type="button"
            className="btn btn-danger btn-xs delete-setting"
            data-setting={compliment.ID}
            onClick={() => {
              if (!window.confirm('Delete this setting?\nYou will not be able to recover this setting!')) return
              remove.mutate()
            }}
          >
            <i className="fas fa-trash" aria-hidden="true"></i>
          </button>
        </div>
      </td>
    </tr>
  )
}

export function FeedbackComplimentsPage() {
  const queryClient = useQueryClient()
  const page = useQuery(complimentsQuery())
  const [creating, setCreating] = useState(false)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'feedback_ticket_compliments'] })
  }

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) {
    return <LoadFailed what="Feedback ticket compliments" error={page.error} onRetry={() => void page.refetch()} />
  }
  if (!page.data) return <Spinner />

  return (
    <div>
      <h4>
        <strong>Feedback</strong> ticket compliments
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage ticket <strong>compliments</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Add compliment
      </button>
      <hr />

      <div className="list-group">
        <table className="table table-hover">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Attribute name</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {page.data.compliments.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center text-muted">
                  No compliments yet. These are the words a customer can tick when praising the person who solved the
                  ticket.
                </td>
              </tr>
            )}
            {page.data.compliments.map((compliment) => (
              <ComplimentRow key={compliment.ID} compliment={compliment} onReload={reload} />
            ))}
          </tbody>
        </table>
      </div>

      <ComplimentModal open={creating} onClose={() => setCreating(false)} onSaved={reload} />
    </div>
  )
}

/** `newComplimentModal` din macheta: numele si starea de pornire. */
export function ComplimentModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [active, setActive] = useState(true)

  useEffect(() => {
    if (!open) return
    setName('')
    setActive(true)
  }, [open])

  const save = useMutation({
    mutationFn: () => createCompliment(name.trim(), active),
    onSuccess: () => {
      toast('Compliment created', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id="newComplimentModal"
      labelledBy="newComplimentModalLabel"
      open={open}
      onClose={onClose}
      title="New feedback compliment"
    >
      <form
        id="new-compliment-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="compliment-name" className="control-label">
            Compliment name:
          </label>
          <input
            type="text"
            id="compliment-name"
            name="compliment_name"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="compliment-status" className="control-label">
            Status:
          </label>
          <select
            className="form-control"
            id="compliment-status"
            name="status"
            value={active ? '1' : '0'}
            onChange={(e) => setActive(e.target.value === '1')}
            required
          >
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-primary submit-new-compliment"
            disabled={name.trim() === '' || save.isPending}
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}
